/**
 * An operation group is a aggregation of operations that each one of them point to a different location.
 * Each operation group has different ordering page.
 */
export interface OperationGroup {
  /**
   * OperationsGroup ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the OperationsGroup is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the OperationsGroup.
   *
   * Ignored when creating a OperationsGroup.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the OperationsGroup was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the OperationsGroup was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * The name of the operations group
   * @minLength 1
   * @maxLength 500
   */
  name?: string | null;
  /**
   * Cover image displayed on the ordering page in the Wix editor and on the published site.
   * @internal
   */
  coverImage?: Image;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups. */
  tags?: Tags;
}

export interface Image {
  /**
   * Image ID.
   * @maxLength 100
   */
  id?: string;
  /**
   * Image URL.
   * @maxLength 200
   */
  url?: string;
  /**
   * Image height in pixels.
   * @min 1
   */
  height?: number;
  /**
   * Image width in pixels.
   * @min 1
   */
  width?: number;
  /**
   * Image alt text.
   * @maxLength 1000
   */
  altText?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface CreateOperationGroupRequest {
  /** OperationGroup to be created. */
  operationGroup: OperationGroup;
  /**
   * The source of this request.
   * @internal
   * @maxLength 50
   */
  source?: string | null;
}

export interface CreateOperationGroupResponse {
  /** The created OperationGroup. */
  operationGroup?: OperationGroup;
}

export interface GetOperationGroupRequest {
  /**
   * ID of the OperationGroup to retrieve.
   * @format GUID
   */
  operationGroupId: string;
}

export interface GetOperationGroupResponse {
  /** The requested OperationGroup. */
  operationGroup?: OperationGroup;
}

export interface UpdateOperationGroupRequest {
  /** OperationGroup to be updated, may be partial. */
  operationGroup: OperationGroup;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateOperationGroupResponse {
  /** Updated OperationGroup. */
  operationGroup?: OperationGroup;
}

export interface DeleteOperationGroupRequest {
  /**
   * Id of the OperationGroup to delete.
   * @format GUID
   */
  operationGroupId: string;
}

export interface DeleteOperationGroupResponse {}

export interface QueryOperationGroupsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryOperationGroupsResponse {
  /** List of OperationGroups. */
  operationGroups?: OperationGroup[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateOperationGroupsRequest {
  /**
   * List of OperationGroups to be created
   * @minSize 1
   * @maxSize 100
   */
  operationGroups: OperationGroup[];
  /** set to `true` if you wish to receive back the created OperationGroups in the response */
  returnEntity?: boolean;
}

export interface BulkCreateOperationGroupsResponse {
  /**
   * List of the bulk create operation results including the OperationGroups and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkOperationGroupResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkOperationGroupResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: OperationGroup;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateOperationGroupsRequest {
  /**
   * List of OperationGroups to be updated.
   * @minSize 1
   * @maxSize 100
   */
  operationGroups: MaskedOperationGroup[];
  /** set to `true` if you wish to receive back the updated OperationGroups in the response */
  returnEntity?: boolean;
}

export interface MaskedOperationGroup {
  /** OperationGroup to be updated, may be partial */
  operationGroup?: OperationGroup;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface BulkUpdateOperationGroupsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateOperationGroupsResponseBulkOperationGroupResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateOperationGroupsResponseBulkOperationGroupResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: OperationGroup;
}

export interface BulkDeleteOperationGroupsRequest {
  /**
   * OperationGroup ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  operationGroupIds: string[];
}

export interface BulkDeleteOperationGroupsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteOperationGroupsResponseBulkOperationGroupResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteOperationGroupsResponseBulkOperationGroupResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateOperationGroupTagsRequest {
  /**
   * IDs of the operation groups to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  operationGroupIds: string[];
  /** Tags to assign to the operation groups. */
  assignTags?: Tags;
  /** Tags to unassign from the operation groups. */
  unassignTags?: Tags;
}

export interface BulkUpdateOperationGroupTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateOperationGroupTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateOperationGroupTagsResult {
  /** Metadata for the updated operation group. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateOperationGroupTagsByFilterRequest {
  /** Filter that determines which operation groups to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the operation groups. */
  assignTags?: Tags;
  /** Tags to unassign from the operation groups. */
  unassignTags?: Tags;
}

export interface BulkUpdateOperationGroupTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

/** @docsIgnore */
export type BulkUpdateOperationGroupTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateOperationGroupTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
